import React, { useState } from 'react';
import { submitToFormspree, formToObject, type FormspreeError } from '../../utils/formspree';
import { ArrowRightIcon, CalendarPlusIcon, CheckIcon, CalendarIcon, ClockIcon, MonitorIcon } from 'lucide-react';
import { useWebinar } from '../../hooks/useWebinar';
import { trackEvent } from '../../utils/analytics';
import { downloadIcs, googleCalendarUrl } from '../../utils/calendar';
import { Flag } from './Flag';
const FORMSPREE_ID = 'xppqqzgy';
type FieldName = 'firstName' | 'lastName' | 'organization' | 'role' | 'email';
const FIELDS: {
  name: FieldName;
  type: string;
  autoComplete: string;
  full?: boolean;
}[] = [{
  name: 'firstName',
  type: 'text',
  autoComplete: 'given-name'
}, {
  name: 'lastName',
  type: 'text',
  autoComplete: 'family-name'
}, {
  name: 'organization',
  type: 'text',
  autoComplete: 'organization',
  full: true
}, {
  name: 'role',
  type: 'text',
  autoComplete: 'organization-title',
  full: true
}, {
  name: 'email',
  type: 'email',
  autoComplete: 'email',
  full: true
}];
export function WebinarRegister() {
  const {
    copy,
    session,
    switchSession
  } = useWebinar();
  const c = copy.register;
  const h = copy.hero;
  const [status, setStatus] = useState<'idle' | 'submitting' | 'succeeded' | 'error'>('idle');
  const [errors, setErrors] = useState<FormspreeError[]>([]);
  const state = {
    submitting: status === 'submitting',
    succeeded: status === 'succeeded'
  };
  const emailError = errors.find(e => e.field === 'email');

  // Posts directly to the Formspree API (dedicated training-registration form).
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrors([]);
    const data = formToObject(e.currentTarget);
    data._replyto = data.email ?? '';
    const result = await submitToFormspree(FORMSPREE_ID, data);
    if (result.ok) {
      setStatus('succeeded');
      trackEvent('webinar_registered', {
        webinar: 'communications-atna',
        session: session.id
      });
    } else {
      setErrors(result.errors);
      setStatus('error');
    }
  }
  return <section id="inscription" aria-labelledby="register-title" className="scroll-mt-[72px] bg-[#F7F7FB]" data-id="element-7959" data-mp="pzmcqnuzmlzb">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-20 items-start" data-id="element-7960" data-mp="14o8rancsj9ph">
        <div className="lg:pt-6" data-id="element-7961" data-mp="zv78435jwo03">
          <h2 id="register-title" className="font-serif text-[2.4rem] md:text-[3.2rem] leading-[1.05] tracking-tight text-[#121A3A]" data-id="element-7962" data-mp="1dof4070o05nl">{c.title}</h2>
          <p className="mt-6 max-w-[30rem] font-sans text-[16.5px] leading-relaxed text-[#4A5168]" data-id="element-7963" data-mp="18p2cva2rgcf7">{c.body}</p>
          <ul className="mt-10 space-y-3" data-id="element-7964" data-mp="1nm35rgwoudf9">
            {[{
            icon: CalendarIcon,
            text: session.date,
            strong: true
          }, {
            icon: ClockIcon,
            text: session.time,
            strong: true
          }, {
            icon: MonitorIcon,
            text: `${h.duration} · ${h.format}`,
            strong: false
          }].map(({
            icon: Icon,
            text,
            strong
          }) => <li key={text} className={`flex items-center gap-3 font-sans text-[15.5px] ${strong ? 'font-semibold text-[#121A3A]' : 'text-[#4A5168]'}`} data-id="element-7965" data-mp="1zwkhjm83wx7z">
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#EEEDFD] text-[#4F46E5]" data-id="element-7966" data-mp="24y9sl1vbxaw2">
                  <Icon className="w-4 h-4" aria-hidden="true" data-id="element-7967" data-mp="1v6dsxmdlhcpq" />
                </span>
                {text}
              </li>)}
          </ul>
        </div>

        <div className="rounded-[24px] bg-white border border-[#E6E7EF] shadow-[0_30px_70px_-40px_rgba(18,26,58,0.35)] p-6 md:p-10" data-id="element-7968" data-mp="1h5b8fe290agx">
          {state.succeeded ? <div role="status" className="py-4" data-id="element-7969" data-mp="179ghlvnvf932">
              <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[#4F46E5] text-white" data-id="element-7970" data-mp="17arm5xp54kv8">
                <CheckIcon className="w-6 h-6" aria-hidden="true" data-id="element-7971" data-mp="31mpm191k84eg" />
              </span>
              <h3 className="mt-6 font-serif text-[1.9rem] leading-tight text-[#121A3A]" data-id="element-7972" data-mp="36lj1h70eq5h9">{c.successTitle}</h3>

              <div className="mt-6 rounded-2xl border border-[#E1E0FB] bg-[#F5F5FF] p-5" data-id="element-7973" data-mp="16jt6osbqmd8n">
                <p className="flex items-center gap-2 font-sans text-[11.5px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5]" data-id="element-7974" data-mp="11kg9bdn8pnrt">
                  <Flag code={session.flag} data-id="element-7975" data-mp="xha5sdriiap1" />
                  {session.label}
                </p>
                <p className="mt-2.5 font-serif text-[1.4rem] leading-tight text-[#121A3A]" data-id="element-7976" data-mp="siqhi6pg4mti">{session.date}</p>
                <p className="mt-1 font-sans text-[16px] font-semibold text-[#4F46E5]" data-id="element-7977" data-mp="nm03rq1463b9">{session.time}</p>
              </div>

              <p className="mt-5 font-sans text-[15px] leading-relaxed text-[#4A5168]" data-id="element-7978" data-mp="irm6fi2w5m3y">{c.successBody}</p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-7979" data-mp="ds9fal4zlsvk">
                <button type="button" onClick={() => {
              downloadIcs({
                title: session.calendarTitle,
                startISO: session.startISO,
                durationMinutes: session.durationMinutes
              });
              trackEvent('webinar_add_to_calendar', {
                type: 'ics',
                session: session.id
              });
            }} className="inline-flex items-center gap-2 rounded-full bg-[#121A3A] px-5 py-3 font-sans text-[14px] font-semibold text-white hover:bg-[#4F46E5] transition-colors duration-150" data-id="element-7980" data-mp="9ulgv8435hyx">
                  <CalendarPlusIcon className="w-4 h-4" aria-hidden="true" data-id="element-7981" data-mp="2pihb3jsw6tq6" />
                  {c.addToCalendar}
                </button>
                <a href={googleCalendarUrl({
              title: session.calendarTitle,
              startISO: session.startISO,
              durationMinutes: session.durationMinutes
            })} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('webinar_add_to_calendar', {
              type: 'google',
              session: session.id
            })} className="inline-flex items-center gap-2 rounded-full border border-[#D9DAE6] px-5 py-3 font-sans text-[14px] font-semibold text-[#121A3A] hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors duration-150" data-id="element-7982" data-mp="1st9r4g7x8sod">
                  {c.googleCalendar}
                </a>
              </div>
            </div> : <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5" data-id="element-7983" data-mp="2ccirmsjb6xeg">
              <input type="hidden" name="form" value="Formation clients — Communications ATNA" data-id="element-7984" data-mp="2r5m2yi6b4ewq" />
              <input type="hidden" name="session" value={session.submissionLabel} data-id="element-7985" data-mp="2m6j45fgx74x7" />
              <input type="hidden" name="language" value={session.id.toUpperCase()} data-id="element-7986" data-mp="1yf409i9hwfju" />
              <input type="hidden" name="_subject" value={c.subject} data-id="element-7987" data-mp="1th2doncv1y3m" />

              {/* Selected session */}
              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[#E1E0FB] bg-[#F5F5FF] px-5 py-4" data-id="element-7988" data-mp="27gv4eioe0ebl">
                <div data-id="element-7989" data-mp="3itnu7nxmasp0">
                  <p className="flex items-center gap-2 font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5]" data-id="element-7990" data-mp="3it4co8bvq6bi">
                    <Flag code={session.flag} className="w-4 h-[11px]" data-id="element-7991" data-mp="3mcmonf0csqn2" />
                    {c.selected} · {session.label}
                  </p>
                  <p className="mt-1.5 font-sans text-[15px] font-semibold text-[#121A3A]" data-id="element-7992" data-mp="3rchav596os1s">
                    {session.date} <span className="text-[#4F46E5]" data-id="element-7993" data-mp="3cg01gjkk1nhv">· {session.time}</span>
                  </p>
                </div>
                <button type="button" onClick={switchSession} className="self-start sm:self-auto font-sans text-[13px] font-semibold text-[#4F46E5] hover:text-[#121A3A] underline-offset-4 hover:underline transition-colors duration-150 whitespace-nowrap" data-id="element-7994" data-mp="3hfmfuwc4d9s9">
                  {c.change}
                </button>
              </div>

              {FIELDS.map(f => <div key={f.name} className={f.full ? 'sm:col-span-2' : ''} data-id="element-7995" data-mp="rw2ktpv0rr58">
                  <label htmlFor={`w-${f.name}`} className="block font-sans text-[13px] font-semibold text-[#121A3A]" data-id="element-7996" data-mp="rurk5ywvezic">
                    {c.fields[f.name]}
                  </label>
                  <input id={`w-${f.name}`} name={f.name} type={f.type} autoComplete={f.autoComplete} required aria-invalid={f.name === 'email' && emailError ? true : undefined} aria-describedby={f.name === 'email' && emailError ? 'w-email-error' : undefined} className="mt-2 w-full rounded-xl border border-[#D9DAE6] bg-white px-4 py-3 font-sans text-[15px] text-[#121A3A] placeholder:text-[#9AA0B4] focus:outline-none focus:border-[#4F46E5] focus:ring-4 focus:ring-[#4F46E5]/10 transition-[border-color,box-shadow] duration-150" data-id="element-7997" data-mp="2ssk0heiwcc7h" />
                  {f.name === 'email' && emailError && <p id="w-email-error" className="mt-1.5 font-sans text-[13px] text-[#B42318]" data-id="element-7998" data-mp="1ra9edef31tdv">
                      {c.emailPrefix} : {emailError.message}
                    </p>}
                </div>)}

              {status === 'error' && <p role="alert" className="sm:col-span-2 font-sans text-[13.5px] text-[#B42318]" data-id="element-7999" data-mp="1h4q80e870buo">{c.error}</p>}

              <div className="sm:col-span-2 pt-2" data-id="element-8000" data-mp="1m31mx3dchcjr">
                <button type="submit" disabled={state.submitting} className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4F46E5] px-7 py-4 font-sans text-[15.5px] font-semibold text-white hover:bg-[#4338CA] disabled:opacity-60 disabled:cursor-wait transition-colors duration-150" data-id="element-8001" data-mp="1qv2w2gf7un5k">
                  {state.submitting ? c.submitting : c.submit}
                  {!state.submitting && <ArrowRightIcon className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-8002" data-mp="29rukt5dj1uh0" />}
                </button>
                <p className="mt-4 text-center font-sans text-[13px] text-[#6B7288]" data-id="element-8003" data-mp="fgz054p2ioxv">{c.note}</p>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}